import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { ArrowRight, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAutosizeTextarea } from '@/lib/hooks/use-autosize-textarea'
import {
  CommandAutocomplete,
  CommandOption,
  filterCommandOptions,
} from '@/components/chat/CommandAutocomplete'
import {
  findKnownSlashToken,
  findTrailingSlashToken,
  replaceSlashToken,
  stripSlashToken,
} from '@/lib/composer/slashCommandToken'

const HOME_COMPOSER_TEXTAREA_MAX_PX = 200
/** Home Lab hero composer — taller ceiling for the larger type scale. */
const HOME_COMPOSER_TEXTAREA_MAX_PX_LG = 280

/**
 * Size variants. `default` is the live homepage and MUST stay byte-identical: every
 * class string below is the untouched original. `lg` is the Home Lab hero composer.
 */
type HomeComposerSize = 'default' | 'lg'

const SIZE_CLASSES: Record<
  HomeComposerSize,
  { shell: string; textarea: string; maxPx: number }
> = {
  default: {
    // Surface classes live HERE, per size, not on the shared wrapper below: a
    // wrapper-level `bg-*` and the `lg` variant's own `bg-*` collide in twMerge
    // and the later one silently wins, which is how the Home Lab composer lost
    // its token and started painting the homepage card instead.
    shell: [
      'min-h-[137px] w-full rounded-2xl px-4 py-4 md:px-6',
      'bg-[var(--home-composer-bg)] shadow-[var(--home-composer-shadow)]',
    ].join(' '),
    textarea: 'max-h-[200px] text-[18px]',
    maxPx: HOME_COMPOSER_TEXTAREA_MAX_PX,
  },
  lg: {
    shell: [
      // Flex column so the min-height's slack is DISTRIBUTED (footer pinned to the
      // bottom via mt-auto) rather than piling up after the last row, which is what
      // margin-only stacking does. `default` keeps normal flow, untouched.
      'flex min-h-[152px] w-full flex-col rounded-2xl px-5 py-4 md:px-6 md:py-5',
      // Held by a hairline rather than a drop shadow, so the card reads as a surface
      // on a near-flat page instead of floating over it.
      'border border-[var(--home-lab-composer-border)] bg-[var(--home-lab-composer-bg)] shadow-none',
    ].join(' '),
    textarea: [
      'max-h-[280px] text-[15px] md:text-[16px]',
      'placeholder:text-[var(--home-lab-composer-placeholder-fg)]',
    ].join(' '),
    maxPx: HOME_COMPOSER_TEXTAREA_MAX_PX_LG,
  },
}

interface HomeComposerProps {
  value: string
  onValueChange: (value: string) => void
  onSubmit: () => void
  placeholder: string
  submitLabel: string
  disabled?: boolean
  rows?: number
  className?: string
  /**
   * Slot AFTER the placeholder, on the same row as the textarea: the armed
   * workflow, the active study type, and later attachments. Kept separate from
   * `footer` because the footer holds the DOORS (pickers you open) while this
   * holds the STATE (what those pickers turned on). Inline and line-height
   * capped so arming a workflow does not grow the first line. Omitted on the
   * live homepage, which renders exactly as before.
   */
  header?: ReactNode
  footer?: ReactNode
  /** Reserve bottom row height (e.g. study mode) so toggling chat/study does not jump layout. */
  reserveFooterSlot?: boolean
  /** When true, allow submitting even with empty text (e.g. image-only sends). */
  hasAttachments?: boolean
  /**
   * Blocks submit WITHOUT dimming the composer. `disabled` is for "this whole
   * surface is unavailable"; this is for "you still owe me a required choice",
   * where the user must stay able to type and to use the footer controls.
   */
  submitBlocked?: boolean
  /**
   * Fired when the user clicks send (or Enter) while `submitBlocked` is true.
   * The button stays clickable so the page can explain what is still owed
   * instead of silently doing nothing.
   */
  onBlockedSubmit?: () => void
  /**
   * A submit that is already running. Blocks re-submission and shows a pending
   * treatment on the button, WITHOUT dimming the surface.
   *
   * Third leg of the split above: `disabled` says the whole surface is
   * unavailable, `submitBlocked` says a required choice is still owed (so the
   * user must stay able to type and use the footer controls), and `isBusy` says
   * the thing the user asked for is already happening. It keeps the same
   * "stay usable" rule as `submitBlocked`, plus the button itself has to show
   * that the click landed.
   */
  isBusy?: boolean
  /** Visual scale. Omit (or 'default') for the live homepage; 'lg' is the Home Lab hero. */
  size?: HomeComposerSize
  /**
   * Slash commands. A leading `/` opens the autocomplete; a `/`-leading submit
   * goes to `onCommand` instead of `onSubmit`. Omit and `/` is ordinary prose.
   */
  commands?: Array<CommandOption>
  onCommand?: (command: string, rest: string) => boolean
  /**
   * Bump to focus the textarea. Used by the sidebar Ask CTA after it lands a
   * fresh Home Lab conversation on a page that was already mounted.
   */
  focusSignal?: number
}

export function HomeComposer({
  value,
  onValueChange,
  onSubmit,
  placeholder,
  submitLabel,
  disabled = false,
  rows = 1,
  className,
  header,
  footer,
  reserveFooterSlot = false,
  hasAttachments = false,
  submitBlocked = false,
  onBlockedSubmit,
  isBusy = false,
  size = 'default',
  commands,
  onCommand,
  focusSignal = 0,
}: HomeComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (focusSignal) {
      textareaRef.current?.focus()
    }
  }, [focusSignal])
  const [isCommandDismissed, setIsCommandDismissed] = useState(false)
  const hasContent = value.trim().length > 0 || hasAttachments
  const canSubmit = hasContent && !disabled && !submitBlocked && !isBusy
  const canNudge = hasContent && !disabled && submitBlocked && !isBusy && Boolean(onBlockedSubmit)
  const showFooterRow = Boolean(footer) || reserveFooterSlot
  const sizeClasses = SIZE_CLASSES[size]
  /* The token being typed, wherever it sits: a `/` opens the list at the start
     of the text or after any whitespace (Slack's convention), and the list
     closes the moment whitespace follows the token, because from then on the
     reader is writing the rest of the message. */
  const trailingToken =
    commands && commands.length > 0 && onCommand && isFocused && !isCommandDismissed
      ? findTrailingSlashToken(value)
      : null
  const commandQuery = trailingToken ? trailingToken.name.slice(1) : null
  const commandRows =
    commandQuery === null ? [] : filterCommandOptions(commands ?? [], commandQuery)
  const hasCommandRows = commandRows.length > 0
  const isExactCommand =
    trailingToken !== null &&
    commandRows.length === 1 &&
    commandRows[0].command.toLowerCase() === trailingToken.name.toLowerCase()

  useAutosizeTextarea(textareaRef, value, {
    maxHeightPx: sizeClasses.maxPx,
    disabled,
  })

  function requestSubmit() {
    const text = value.trim()
    /* A known `/command` anywhere on the line names the command and the rest
       of the line is the message; a leading unknown one is still refused by
       the handler rather than sent as prose. */
    const token = onCommand
      ? findKnownSlashToken(
          text,
          (commands ?? []).map((option) => option.command),
        )
      : null
    if (onCommand && token) {
      const rest = stripSlashToken(text, token)
      if (!onCommand(token.name, rest)) {
        return
      }
      /* Only the token leaves the box. The words around it stay on screen
         while the command's confirm card is open, so the reader still sees
         what they wrote, and cancelling the card loses nothing. */
      onValueChange(rest)
      return
    }
    if (canSubmit) {
      onSubmit()
      return
    }
    if (canNudge) {
      onBlockedSubmit?.()
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      hasCommandRows &&
      !event.shiftKey &&
      (event.key === 'ArrowUp' ||
        event.key === 'ArrowDown' ||
        ((event.key === 'Enter' || event.key === 'Tab') && !isExactCommand))
    ) {
      return
    }

    /* Taking Enter/Tab back off a dropdown that is still on screen (the
       exact-match case). The dropdown listens on `document`, above React's
       root, so without stopping propagation it would ALSO see this key, pick
       the row, and write the command back into the box just cleared. Same
       guard as `ChatComposer`. */
    if (
      (event.key === 'Enter' || event.key === 'Tab') &&
      !event.shiftKey &&
      hasCommandRows &&
      isExactCommand
    ) {
      event.preventDefault()
      event.stopPropagation()
      requestSubmit()
      return
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      requestSubmit()
    }
  }

  return (
    <div
      className={cn(
        sizeClasses.shell,
        'text-[var(--home-composer-fg)]',
        commandQuery !== null && 'relative',
        disabled && 'opacity-50 pointer-events-none',
        className,
      )}
    >
      {commandQuery !== null ? (
        <CommandAutocomplete
          options={commands ?? []}
          query={commandQuery}
          onSelect={(option) => {
            /* Replace the typed token in place (trailing space closes the
               list) so the words before it survive as the message. */
            onValueChange(
              trailingToken
                ? replaceSlashToken(value, trailingToken, `${option.command} `)
                : `${option.command} `,
            )
            setIsCommandDismissed(false)
          }}
          onDismiss={() => setIsCommandDismissed(true)}
          appearance={size === 'lg' ? 'internal' : 'external'}
        />
      ) : null}

      <div
        className={cn(
          'relative flex min-w-0 items-center gap-2',
          showFooterRow ? 'mb-4' : 'mb-6',
        )}
        data-testid="home-composer-input-row"
      >
        <textarea
          ref={textareaRef}
          data-testid="home-composer-input"
          value={value}
          onChange={(event) => {
            onValueChange(event.target.value)
            setIsCommandDismissed(false)
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          rows={rows}
          disabled={disabled}
          className={cn(
            'min-h-0 min-w-0 flex-1 resize-none border-0 bg-transparent p-0 leading-[1.5] outline-none focus:outline-none focus:ring-0',
            sizeClasses.textarea,
            'placeholder:text-[var(--home-composer-placeholder-fg)]',
          )}
          placeholder={placeholder}
        />

        {header ? (
          <div
            className="flex h-[1.5em] shrink-0 items-center gap-1.5 self-start"
            data-testid="home-composer-header"
          >
            {header}
          </div>
        ) : null}
      </div>

      <div
        className={cn(
          'flex items-end gap-2 md:gap-3',
          showFooterRow ? 'justify-between' : 'justify-end',
          size === 'lg' && 'mt-auto',
        )}
      >
        {footer ? (
          <div
            className={cn(
              'min-w-0 flex-1',
              /* Internal pills pad themselves (`px-3`). Pull the row back so the
                 first icon lines up with the placeholder, not with the pill edge. */
              size === 'lg' && '-ml-1 md:-ml-3',
            )}
          >
            {footer}
          </div>
        ) : reserveFooterSlot ? (
          <div className="min-h-11 min-w-0 flex-1 shrink-0" aria-hidden />
        ) : null}

        <button
          type="button"
          onClick={requestSubmit}
          disabled={!canSubmit && !canNudge}
          aria-label={submitLabel}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-cta text-cta-foreground transition-[transform,opacity] duration-200 hover:-translate-y-0.5 hover:opacity-90 disabled:pointer-events-none disabled:opacity-40 md:h-12 md:w-12 md:rounded-lg"
        >
          {isBusy ? (
            <Loader2
              className="h-4 w-4 animate-spin md:h-5 md:w-5"
              strokeWidth={2.2}
              aria-hidden
              data-testid="home-composer-submit-pending"
            />
          ) : (
            <ArrowRight className="h-4 w-4 md:h-5 md:w-5" strokeWidth={2.2} />
          )}
        </button>
      </div>
    </div>
  )
}
